import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment/moment";
import { App, Button, List, Segmented } from "antd";

import { ExclamationCircleFilled } from "@ant-design/icons";
import { DeleteOutlined } from "@ant-design/icons";
import ProfileAvatar from "components/UI/ProfileAvatar";

import { apiDeleteModel } from "api/apiAiModels";
import GenericCard from "components/UI/GenericCard/GenericCard";
import PredictButton from "./PredictButton";
import ProtectedContent from "components/UI/ProtectedContent";
import DashedTooltip from "components/UI/DashedTooltip";
import ConsultAiForm from "./ConsultAiForm";
import VirtualList from "rc-virtual-list";
import { toFixed } from "utils/helpers";

const NOT_RATES = ["lift", "roc_auc", "test_rows", "effective_samples"];
const ChooseModel = (props) => {
  const {
    form,
    modelsList,
    getModelsList,
    handleCheckAiSymbol,
    resolution,
    setResolution,
  } = props;
  const { message, modal } = App.useApp();
  const [isSearchAllPending, setIsSearchAllPending] = useState(false);
  const [deleteModelPending, setDeleteModelPending] = useState(false);

  const deleteModelById = async (modelId) => {
    setDeleteModelPending(true);
    try {
      await apiDeleteModel(modelId);
      getModelsList(resolution);
      message.success("Model deleted successfully");
    } catch (err) {
      message.error(err.response.data.message);
    }
    setDeleteModelPending(false);
  };

  const showDeleteConfirm = (modelId) => {
    modal.confirm({
      title: "Are you sure you want to delete this model?",
      icon: <ExclamationCircleFilled />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        deleteModelById(modelId);
      },
      onCancel() {},
    });
  };

  const onSearchAllSuccess = () => {
    setIsSearchAllPending(false);
    message.success("Search all finished successfully");
  };

  const onSearchAllFailure = (errorMessage = "Search all failed") => {
    setIsSearchAllPending(false);
    message.error(errorMessage);
  };

  const handleSearchAllModels = async () => {
    setIsSearchAllPending(true);

    try {
      await handleCheckAiSymbol();
      onSearchAllSuccess();
    } catch (err) {
      onSearchAllFailure(err?.response?.data?.message);
    }
  };

  return (
    <GenericCard title="Predict with AI" maxHeight={1000}>
      <div className="flex gap-2 lg:gap-8 flex-wrap">
        <ConsultAiForm form={form} />
        <div className="flex flex-col justify-between items-stretch">
          <p className="mb-3">
            <span className="pe-2">Resolution:</span>
            <Segmented
              value={resolution}
              onChange={(value) => setResolution(value)}
              options={[
                { label: "1D", value: "1d" },
                { label: "1H", value: "1h" },
              ]}
            />
          </p>
          <ProtectedContent requiredPermissions={["manager"]}>
            <div className="results-table-buttons">
              <Button
                disabled={isSearchAllPending || modelsList.length === 0}
                onClick={handleSearchAllModels}
              >
                Search all models ({modelsList.length})
              </Button>
            </div>
          </ProtectedContent>
        </div>
      </div>

      <List bordered size="small">
        <VirtualList
          data={modelsList}
          itemHeight={160}
          height={500}
          itemKey="_id"
        >
          {(item) => (
            <List.Item
              actions={[
                item.usedByStrategies === 0 && (
                  <ProtectedContent requiredPermissions={["manager"]}>
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={() => showDeleteConfirm(item._id)}
                      danger
                      ghost
                      loading={deleteModelPending}
                    />
                  </ProtectedContent>
                ),
                <PredictButton
                  model={item}
                  handleCheckAiSymbol={handleCheckAiSymbol}
                  isDisabled={isSearchAllPending}
                />,
              ]}
            >
              <List.Item.Meta
                avatar={<ProfileAvatar user={item?.creator} />}
                title={item.name}
                description={
                  <>
                    <div className="flex">
                      <div className="pe-2">
                        <DashedTooltip
                          text={item?.results?.stocks?.map((item, index) =>
                            index === 0 ? item : `, ${item}`
                          )}
                        >
                          <span className="">Trained data:</span>
                        </DashedTooltip>
                      </div>
                      <div className="flex gap-2" style={{ color: "#ffffff" }}>
                        <div>
                          {moment(item.results?.start_date).format("DD/MM/YY")}
                        </div>
                        {" - "}
                        <div>
                          {moment(item.results?.end_date).format("DD/MM/YY")}
                        </div>
                      </div>
                    </div>
                    <p>
                      Profit target:{" "}
                      <span style={{ color: "#ffffff" }}>
                        {((item.results?.perc_to_gain - 1) * 100).toFixed(2)}%
                        in {item.results?.days} bars
                      </span>
                    </p>
                    <p>
                      <span>Buy/total: </span>
                      <span style={{ color: "#ffffff" }} className="pe-4">
                        {(item.results?.buy_from_total * 100).toFixed(1)}%
                      </span>

                      {item.results?.evaluation_metrics ? (
                        Object.entries(item.results?.evaluation_metrics).map(
                          ([key, value]) => {
                            // only rates are shown here, as percentages
                            if (typeof value !== "number" || NOT_RATES.includes(key)) {
                              return null;
                            }
                            return (
                              <span>
                                {key.replaceAll("_", " ")}:{" "}
                                <span
                                  style={{ color: "#ffffff" }}
                                  className="pe-4"
                                >
                                  {toFixed(value * 100, 2)}%
                                </span>
                              </span>
                            );
                          }
                        )
                      ) : (
                        <>
                          <span>Accuarcy: </span>
                          <span style={{ color: "#ffffff" }} className="pe-4">
                            {(item.results?.accuracy * 100).toFixed(1)}%
                          </span>
                        </>
                      )}

                      <span>Type: </span>
                      <span style={{ color: "#ffffff" }} className="pe-4">
                        {item.modelType?.toUpperCase() || "N/A"}
                      </span>
                      <span>Resolution: </span>
                      <span style={{ color: "#ffffff" }} className="pe-4">
                        {item.resolution?.toUpperCase() || "N/A"}
                      </span>
                    </p>
                    <p>
                      Indicators:{" "}
                      {item.results?.indicators &&
                        Object.entries(item.results.indicators).map(
                          ([indicator, value]) => {
                            if (typeof value === "object" && value !== null) {
                              return (
                                <span
                                  style={{ color: "#ffffff" }}
                                  className="pe-4 inline-flex"
                                >
                                  {indicator}:{" "}
                                  {Object.values(value).map(
                                    (value) => `${value} `
                                  )}
                                </span>
                              );
                            }
                            return (
                              <span
                                style={{ color: "#ffffff" }}
                                className="pe-4 inline-flex"
                              >
                                {indicator}: {value}
                              </span>
                            );
                          }
                        )}
                    </p>
                    <p>
                      {item?.creator
                        ? `Created by ${item.creator.firstName} ${item?.creator?.lastName}, `
                        : ""}
                      {moment(item.date).format("HH:mm DD/MM/YY")}
                      {item.usedByStrategies > 0 && (
                        <span style={{ color: "#ffffff" }} className="ps-4">
                          Used by {item.usedByStrategies} strategies
                        </span>
                      )}
                    </p>
                  </>
                }
              />
            </List.Item>
          )}
        </VirtualList>
      </List>
    </GenericCard>
  );
};

ChooseModel.propTypes = {
  form: PropTypes.object.isRequired,
  setChoosenModel: PropTypes.func,
  choosenModel: PropTypes.object,
};

export default ChooseModel;
